import type { DeviceSession } from '@/domain/entities/DeviceSession';

/**
 * Espelho da sessão de dispositivo ativa, persistido na object store
 * `activeSession` do IndexedDB (chave fixa única) — ADR-003, Adendo
 * 2026-07-25 (Correção 2) e Spec Técnica > Correção 3.
 *
 * **Deixou de ser leitura exclusiva do Service Worker** (ADR-003, Adendo
 * 2026-07-30 — Correção 4): o main thread (`useActivationFlow`, ao montar)
 * também lê este snapshot, para restaurar a sessão de dispositivo após um
 * refresh de página sem exigir novo PIN (LOCAL-003). Os dois leitores
 * coexistem sem conflito — nenhum dos dois escreve fora de
 * `AuthPinUseCase.mirrorActiveSession`.
 *
 * Motivação original (Service Worker): Background Sync dispara mesmo com a
 * aba fechada; o handler `sync` roda em `ServiceWorkerGlobalScope`, sem
 * acesso ao estado em memória do React onde `DeviceSession` normalmente
 * vive. Este snapshot é a única forma do SW montar o header `Authorization`
 * da sincronização em segundo plano.
 *
 * Superset mínimo de campos de `DeviceSession`: apenas os usados por
 * `HttpLeadRepository.captureLead`/`HttpEventConfigRepository.load`
 * (`eventId`, `deviceId`, `jwtToken`) mais `expiresAt`, usado para não
 * tentar sincronizar/restaurar com um token já expirado.
 */
export interface ActiveSessionSnapshot {
  readonly jwtToken: string;
  readonly eventId: string;
  readonly deviceId: string;
  readonly expiresAt: string;
}

/** Constrói o snapshot a partir da `DeviceSession` em memória — chamado
 * sempre que ela muda (login por PIN, HUB-235/HUB-236). */
export function toActiveSessionSnapshot(session: DeviceSession): ActiveSessionSnapshot {
  return {
    jwtToken: session.jwtToken,
    eventId: session.eventId,
    deviceId: session.deviceId,
    expiresAt: session.expiresAt.toISOString(),
  };
}

/** `true` quando o token já expirou e não deve ser usado para autenticar
 * uma tentativa de sincronização em segundo plano nem para restaurar a
 * sessão no boot (LOCAL-003). */
export function isActiveSessionExpired(snapshot: ActiveSessionSnapshot, now: Date): boolean {
  return new Date(snapshot.expiresAt).getTime() <= now.getTime();
}

/**
 * Reconstrói uma `DeviceSession` equivalente a partir do snapshot leve —
 * reaproveitada por `SyncLeadQueueUseCase` (autenticar a sincronização em
 * segundo plano) e por `useActivationFlow` (restaurar a sessão no boot,
 * ADR-003 Adendo 2026-07-30 — Correção 4). `agencyId`/`startedAt` não fazem
 * parte do snapshot — não são lidos por `HttpLeadRepository.captureLead` nem
 * por `HttpEventConfigRepository.load`/`HttpPluginRegistryRepository`
 * (todos usam só `eventId`/`deviceId`/`jwtToken`) — preenchidos aqui com
 * valores neutros só para satisfazer o tipo `DeviceSession` sem alterá-lo.
 */
export function toDeviceSessionFromSnapshot(snapshot: ActiveSessionSnapshot): DeviceSession {
  return {
    jwtToken: snapshot.jwtToken,
    eventId: snapshot.eventId,
    deviceId: snapshot.deviceId,
    agencyId: '',
    startedAt: new Date(0),
    expiresAt: new Date(snapshot.expiresAt),
  };
}
